export const PROD = 'prod'
export const TESTING = 'testing'
export const HOTFIX = 'hotfix'
export const REVIEW = 'review'
export const INTERNAL = 'internal'
export const INTERNAL_TESTING = 'internal_testing'

export type Environment =
  | typeof PROD
  | typeof TESTING
  | typeof HOTFIX
  | typeof REVIEW
  | typeof INTERNAL
  | typeof INTERNAL_TESTING

export const devEnvs: Environment[] = [TESTING]
export const internalEnvs: Environment[] = [INTERNAL, INTERNAL_TESTING]
export const prodEnvs: Environment[] = [HOTFIX, PROD, REVIEW]
export const nonInternalEnvs: Environment[] = [HOTFIX, PROD, REVIEW, TESTING]
export const chtcEnvs: Environment[] = [HOTFIX, PROD, REVIEW, TESTING, INTERNAL_TESTING, INTERNAL]
export const nonInternalChtcEnvs: Environment[] = [HOTFIX, PROD, REVIEW, TESTING]

/**
 * Salesforce connection settings of a single deployment environment.
 */
export interface EnvironmentConfig {
  /** Salesforce org URL used as the base for REST API calls. */
  instanceUrl: string
  /** Experience Cloud site (or standard login page for internal envs) the OAuth flow starts on. */
  loginUrl: string
  /** Consumer key of the Connected App - sandboxes have their own apps. */
  clientId: string
  /** Public URL of the deployment. */
  redirectUri: string
  /** Google Analytics measurement ID. */
  gaConfig: string
  /** Marks a connection to a sandbox */
  sandbox?: boolean
}

const configs: Record<Environment, EnvironmentConfig> = {
  [PROD]: {
    instanceUrl: 'https://chtc-ctlc.my.salesforce.com',
    loginUrl: ' https://chtc-ctlc.my.site.com/APIConnectionPortal/',
    clientId:
      '3MVG9nkapUnZB56EDSefar0nDpAV8iiLs5xo8zilpKveJAWRPx3Q0t4xShdTGu.B8hf85N.SIM7qHytNhoKP2',
    redirectUri: 'https://community.centre.support',
    gaConfig: 'G-HPQ0V1375K'
  },
  // same connection as prod just with new code
  [HOTFIX]: {
    instanceUrl: 'https://chtc-ctlc.my.salesforce.com',
    loginUrl: 'https://chtc-ctlc.my.site.com/APIConnectionPortal/',
    clientId:
      '3MVG9nkapUnZB56EDSefar0nDpAV8iiLs5xo8zilpKveJAWRPx3Q0t4xShdTGu.B8hf85N.SIM7qHytNhoKP2',
    redirectUri: 'https://hotfix.centre.support',
    gaConfig: 'G-HPQ0V1375K'
  },
  [REVIEW]: {
    instanceUrl: 'https://chtc-ctlc.my.salesforce.com',
    loginUrl: 'https://chtc-ctlc.my.site.com/APIConnectionPortal/',
    clientId:
      '3MVG9nkapUnZB56EDSefar0nDpAV8iiLs5xo8zilpKveJAWRPx3Q0t4xShdTGu.B8hf85N.SIM7qHytNhoKP2',
    redirectUri: 'https://review.centre.support',
    gaConfig: 'G-HPQ0V1375K'
  },
  [INTERNAL]: {
    instanceUrl: 'https://chtc-ctlc.my.salesforce.com',
    loginUrl: 'https://chtc-ctlc.lightning.force.com/',
    clientId:
      '3MVG9nkapUnZB56EDSefar0nDpAV8iiLs5xo8zilpKveJAWRPx3Q0t4xShdTGu.B8hf85N.SIM7qHytNhoKP2',
    redirectUri: 'https://internal.centre.support',
    gaConfig: 'G-HPQ0V1375K'
  },
  [TESTING]: {
    instanceUrl: 'https://chtc-ctlc--testing.sandbox.my.salesforce.com',
    loginUrl: 'https://chtc-ctlc--testing.sandbox.my.site.com/APIConnectionPortal',
    clientId:
      '3MVG96qSbA1.IAaxiM3VVZEWzBSoiQBhna33mGk6ItSjC9MjmXDjrpX0mJ2i9mGtDDtoh_jwHagbTWDEWPzew',
    redirectUri: 'https://testing.centre.support',
    gaConfig: 'G-XMNP6TZ223',
    sandbox: true
  },
  [INTERNAL_TESTING]: {
    instanceUrl: 'https://chtc-ctlc--testing.sandbox.my.salesforce.com',
    loginUrl: 'https://test.salesforce.com',
    clientId:
      '3MVG96qSbA1.IAaxiM3VVZEWzBSoiQBhna33mGk6ItSjC9MjmXDjrpX0mJ2i9mGtDDtoh_jwHagbTWDEWPzew',
    redirectUri: 'https://internaltesting.centre.support',
    gaConfig: 'G-XMNP6TZ223',
    sandbox: true
  }
}

// Unknown or missing env name falls back to prod below, hence the cast
let current_config: EnvironmentConfig = configs[process.env.REACT_APP_ENVIROMENT as Environment]

if (!current_config) {
  current_config = configs[PROD]
}

export const isSandbox = (): boolean => current_config.sandbox === true

const sfOauthConfig = {
  scope: 'web api id',
  response_type: 'code',
  instanceUrl: current_config.instanceUrl,
  // you can change loginUrl to connect to sandbox or prerelease env.
  oauth2: {
    loginUrl: current_config.loginUrl,
    clientId: current_config.clientId,
    // clientSecret: current_config.clientSecret,
    redirectUri:
      process.env.NODE_ENV === 'development'
        ? 'http://localhost:3000'
        : // : current_config.redirectUri,
          `${window.location.protocol}//${window.location.host}`
  },
  config: current_config,
  // Substring matches on purpose - e.g. 'internal_testing' counts as both internal and testing
  isLive: Boolean(
    process.env.REACT_APP_ENVIROMENT?.includes('live') ||
    process.env.REACT_APP_ENVIROMENT?.includes('prod')
  ),
  isInternal: Boolean(process.env.REACT_APP_ENVIROMENT?.includes('internal')),
  isReview: Boolean(process.env.REACT_APP_ENVIROMENT?.includes(REVIEW)),
  isTesting: Boolean(process.env.REACT_APP_ENVIROMENT?.includes(TESTING)),
  currentEnv: process.env.REACT_APP_ENVIROMENT
}

export default sfOauthConfig
